
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface TrailerModalProps {
  isOpen: boolean;
  onClose: () => void;
  drama: {
    title: string;
    trailerUrl: string;
  } | null;
}

function TrailerModal({ isOpen, onClose, drama }: TrailerModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !drama) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      <div className="relative w-full max-w-5xl z-10" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 cursor-pointer"
        >
          <span className="text-sm">Close</span>
          <div className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <i className="ri-close-line text-xl"></i>
          </div>
        </button>
        <div className="mb-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-center">
            <i className="ri-play-fill text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-white font-bold text-xl">{drama.title}</h3>
            <p className="text-amber-400/80 text-sm">Official Trailer</p>
          </div>
        </div>
        <div className="relative rounded-xl overflow-hidden bg-black shadow-2xl shadow-amber-500/10 border border-amber-500/20">
          <div className="aspect-video w-full">
            <iframe
              src={drama.trailerUrl}
              title={`${drama.title} Trailer`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const featuredDramas = [
  {
    title: 'Noor',
    subtitle: 'A Journey of Light',
    description:
      'Follow the inspiring story of Noor as she navigates love, family, and destiny in modern-day Cairo. A powerful tale of resilience and hope.',
    episodes: '24 Episodes',
    rating: '9.6',
    genre: 'Drama',
    trailerUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    backgroundImage:
      'https://public.readdy.ai/ai/img_res/dbe2841c9c8e7c3566b7c3b2b3b8a351.jpg',
    cardImage:
      'https://readdy.ai/api/search-image?query=cinematic%20portrait%20of%20elegant%20woman%20wearing%20flowing%20golden%20silk%20dress%20standing%20in%20ornate%20arabian%20palace%20doorway%20warm%20amber%20backlight%20dramatic%20shadows%20professional%20film%20photography%20emotional%20depth&width=480&height=640&seq=hero-redesign-noor-card-v3&orientation=portrait',
  },
  {
    title: 'Desert Rose',
    subtitle: 'Love Blooms in the Sands',
    description:
      'A tale of love and betrayal set against the breathtaking backdrop of ancient Arabia. Experience passion, honor, and sacrifice in this epic romantic drama.',
    episodes: '16 Episodes',
    rating: '9.2',
    genre: 'Romance',
    trailerUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    backgroundImage:
      'https://readdy.ai/api/search-image?query=cinematic%20sweeping%20aerial%20view%20of%20vast%20golden%20sand%20dunes%20at%20sunset%20with%20ancient%20arabian%20fortress%20silhouette%20on%20horizon%20warm%20orange%20and%20deep%20crimson%20sky%20dramatic%20long%20shadows%20professional%20film%20photography%20ultra%20wide%20composition%20atmospheric%20haze&width=1920&height=900&seq=hero-redesign-desert-bg-v3&orientation=landscape',
    cardImage:
      'https://readdy.ai/api/search-image?query=cinematic%20scene%20of%20romantic%20couple%20silhouette%20standing%20on%20sand%20dune%20overlooking%20ancient%20arabian%20city%20at%20golden%20hour%20warm%20amber%20tones%20flowing%20traditional%20garments%20wind%20blowing%20professional%20film%20photography&width=480&height=640&seq=hero-redesign-desert-card-v3&orientation=portrait',
  },
  {
    title: 'City Lights',
    subtitle: 'Dreams in the Metropolis',
    description:
      'In the heart of Dubai, ambition meets destiny. Follow the intertwined lives of dreamers chasing success in the glittering cityscape.',
    episodes: '18 Episodes',
    rating: '9.0',
    genre: 'Thriller',
    trailerUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    backgroundImage:
      'https://readdy.ai/api/search-image?query=cinematic%20nighttime%20aerial%20view%20of%20modern%20middle%20eastern%20metropolis%20with%20glowing%20warm%20amber%20streetlights%20luxury%20skyscrapers%20reflecting%20golden%20light%20on%20wet%20roads%20after%20rain%20dramatic%20atmosphere%20professional%20film%20photography%20moody%20teal%20and%20amber%20color%20grading&width=1920&height=900&seq=hero-redesign-city-bg-v3&orientation=landscape',
    cardImage:
      'https://readdy.ai/api/search-image?query=cinematic%20portrait%20of%20confident%20man%20in%20tailored%20suit%20standing%20on%20rooftop%20overlooking%20modern%20city%20skyline%20at%20night%20warm%20amber%20city%20lights%20bokeh%20background%20professional%20film%20photography%20dramatic%20lighting&width=480&height=640&seq=hero-redesign-city-card-v3&orientation=portrait',
  },
  {
    title: 'Whispers of Time',
    subtitle: 'Echoes from the Past',
    description:
      'When ancient secrets resurface in a historic palace, a young archaeologist uncovers mysteries that bridge centuries. A thrilling blend of history and suspense.',
    episodes: '20 Episodes',
    rating: '9.4',
    genre: 'Mystery',
    trailerUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    backgroundImage:
      'https://readdy.ai/api/search-image?query=cinematic%20interior%20of%20ancient%20mysterious%20library%20with%20towering%20bookshelves%20ornate%20golden%20candelabras%20warm%20candlelight%20casting%20dramatic%20shadows%20on%20stone%20walls%20dust%20particles%20floating%20in%20light%20beams%20atmospheric%20fog%20professional%20film%20photography&width=1920&height=900&seq=hero-redesign-whispers-bg-v3&orientation=landscape',
    cardImage:
      'https://readdy.ai/api/search-image?query=cinematic%20scene%20of%20young%20woman%20archaeologist%20holding%20ancient%20golden%20artifact%20in%20dimly%20lit%20stone%20chamber%20warm%20candlelight%20mysterious%20atmosphere%20dust%20particles%20professional%20film%20photography%20dramatic%20chiaroscuro%20lighting&width=480&height=640&seq=hero-redesign-whispers-card-v3&orientation=portrait',
  },
];

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [animState, setAnimState] = useState<'enter' | 'exit'>('enter');
  const [displayIndex, setDisplayIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const changeSlide = (newIndex: number) => {
    if (newIndex === displayIndex || animState === 'exit') return;
    setAnimState('exit');
    setActiveIndex(newIndex);
    setTimeout(() => {
      setDisplayIndex(newIndex);
      setAnimState('enter');
    }, 450);
  };

  const goNext = () => {
    changeSlide((displayIndex + 1) % featuredDramas.length);
  };

  const goPrev = () => {
    changeSlide((displayIndex - 1 + featuredDramas.length) % featuredDramas.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const dist = touchStartX.current - touchEndX.current;
    if (dist > 50) goNext();
    else if (dist < -50) goPrev();
    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    if (isPaused || animState === 'exit') return;
    timerRef.current = setInterval(goNext, 6000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, animState, displayIndex]);

  const current = featuredDramas[displayIndex];

  return (
    <section
      className="relative w-full min-h-[720px] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <style>{`
        @keyframes heroFadeIn {
          from { opacity: 0; transform: scale(1.06); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes heroFadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(1.03); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-24px); }
        }
        @keyframes cardReveal {
          from { opacity: 0; transform: translateY(50px) scale(0.92); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cardHide {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(-30px) scale(0.95); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        .hero-bg-enter { animation: heroFadeIn 1s ease-out forwards; }
        .hero-bg-exit { animation: heroFadeOut 0.45s ease-in forwards; }
        .text-enter { animation: slideUp 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
        .text-exit { animation: slideDown 0.4s ease-in forwards; }
        .card-enter { animation: cardReveal 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
        .card-exit { animation: cardHide 0.4s ease-in forwards; }
        .delay-1 { animation-delay: 0.08s; }
        .delay-2 { animation-delay: 0.16s; }
        .delay-3 { animation-delay: 0.24s; }
        .delay-4 { animation-delay: 0.32s; }
        .shimmer-line {
          background: linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.15) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: shimmer 3s infinite;
        }
        .float-slow { animation: floatSlow 6s ease-in-out infinite; }
      `}</style>

      {/* Background Image */}
      <div
        key={`bg-${displayIndex}`}
        className={`absolute inset-0 bg-cover bg-center ${animState === 'exit' ? 'hero-bg-exit' : 'hero-bg-enter'}`}
        style={{ backgroundImage: `url(${current.backgroundImage})` }}
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />

      {/* Subtle grain texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }}
      />

      {/* Navigation Arrows */}
      <button
        onClick={goPrev}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all cursor-pointer group"
      >
        <i className="ri-arrow-left-s-line text-2xl group-hover:-translate-x-0.5 transition-transform" />
      </button>
      <button
        onClick={goNext}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all cursor-pointer group"
      >
        <i className="ri-arrow-right-s-line text-2xl group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[720px] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full py-20">
          {/* Left: Text Content */}
          <div className="lg:col-span-7">
            <div
              key={`label-${displayIndex}`}
              className={`flex items-center gap-3 mb-6 opacity-0 ${animState === 'exit' ? 'text-exit' : 'text-enter'}`}
            >
              <div className="shimmer-line h-[1px] w-10" />
              <span className="text-amber-400/90 text-xs tracking-[0.25em] uppercase font-semibold">
                {current.genre} &middot; {current.episodes} &middot; ★ {current.rating}
              </span>
              <div className="shimmer-line h-[1px] w-10" />
            </div>

            <div key={`title-${displayIndex}`}>
              <h2
                className={`text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-3 opacity-0 ${animState === 'exit' ? 'text-exit' : 'text-enter delay-1'}`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <span className="text-white">{current.title}</span>
              </h2>
              <p
                className={`text-2xl md:text-3xl font-light text-amber-300/90 mb-6 opacity-0 ${animState === 'exit' ? 'text-exit' : 'text-enter delay-2'}`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {current.subtitle}
              </p>
            </div>

            <p
              key={`desc-${displayIndex}`}
              className={`text-base md:text-lg text-white/70 max-w-lg leading-relaxed mb-8 opacity-0 ${animState === 'exit' ? 'text-exit' : 'text-enter delay-3'}`}
            >
              {current.description}
            </p>

            <div
              key={`cta-${displayIndex}`}
              className={`flex flex-wrap items-center gap-4 opacity-0 ${animState === 'exit' ? 'text-exit' : 'text-enter delay-4'}`}
            >
              <button
                onClick={() => setIsTrailerOpen(true)}
                className="group flex items-center gap-3 px-7 py-3.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-fill text-lg group-hover:scale-110 transition-transform" />
                </div>
                Watch Trailer
              </button>
              <Link
                to="/dramas"
                className="flex items-center gap-3 px-7 py-3.5 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-medium rounded-lg border border-white/20 transition-all whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-compass-3-line text-lg" />
                </div>
                Explore All Dramas
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-8 mt-10">
              {[
                { icon: 'ri-film-line', value: '200+', label: 'Dramas' },
                { icon: 'ri-user-star-line', value: '5K+', label: 'Creators' },
                { icon: 'ri-group-line', value: '50K+', label: 'Members' },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                    <i className={`${stat.icon} text-amber-400 text-lg`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm leading-tight">{stat.value}</p>
                    <p className="text-white/50 text-xs">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Featured Card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div
              key={`card-${displayIndex}`}
              className={`relative float-slow opacity-0 ${animState === 'exit' ? 'card-exit' : 'card-enter delay-2'}`}
            >
              <div className="absolute -inset-6 bg-amber-500/10 rounded-3xl blur-3xl" />
              <div
                className="relative w-[300px] md:w-[340px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 group cursor-pointer"
                onClick={() => setIsTrailerOpen(true)}
              >
                <div className="w-[300px] md:w-[340px] h-[420px] md:h-[460px] overflow-hidden">
                  <img
                    src={current.cardImage}
                    alt={current.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <i className="ri-play-fill text-white text-3xl ml-1" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2.5 py-0.5 bg-amber-500 text-black text-[11px] font-bold rounded-full uppercase tracking-wide">
                      Featured
                    </span>
                    <span className="px-2.5 py-0.5 bg-white/15 backdrop-blur-sm text-white text-[11px] rounded-full">
                      {current.genre}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-lg">{current.title}</h3>
                  <p className="text-amber-400/80 text-sm">{current.subtitle}</p>
                  <div className="flex items-center gap-3 mt-2.5">
                    <span className="text-white/60 text-xs flex items-center gap-1">
                      <i className="ri-movie-2-line text-sm" />
                      {current.episodes}
                    </span>
                    <span className="text-amber-400 text-xs flex items-center gap-1">
                      <i className="ri-star-fill text-sm" />
                      {current.rating}
                    </span>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -left-8 w-20 h-28 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm -rotate-12 -z-10 hidden lg:block" />
              <div className="absolute -bottom-3 -right-6 w-16 h-24 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm rotate-6 -z-10 hidden lg:block" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-6">
        {featuredDramas.map((drama, index) => (
          <button
            key={index}
            onClick={() => changeSlide(index)}
            className="group relative flex flex-col items-center gap-2 cursor-pointer"
          >
            <div
              className={`relative w-14 h-9 rounded-md overflow-hidden transition-all duration-300 ${
                activeIndex === index
                  ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-black/50 scale-110'
                  : 'opacity-50 hover:opacity-80 grayscale hover:grayscale-0'
              }`}
            >
              <img src={drama.cardImage} alt={drama.title} className="w-full h-full object-cover" />
            </div>
            <span
              className={`text-[10px] font-medium transition-all duration-300 whitespace-nowrap ${
                activeIndex === index ? 'text-amber-400 opacity-100' : 'text-white/0 opacity-0'
              }`}
            >
              {drama.title}
            </span>
            {activeIndex === index && !isPaused && (
              <div className="absolute -bottom-1 left-0 right-0 h-[2px] bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full"
                  style={{ animation: 'progressFill 6s linear forwards' }}
                />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Progress bar keyframe */}
      <style>{`
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

      {/* Trailer Modal */}
      <TrailerModal isOpen={isTrailerOpen} onClose={() => setIsTrailerOpen(false)} drama={current} />
    </section>
  );
}
